import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellOff, Loader2, CheckCircle2, AlertCircle, Send } from "lucide-react";

function readCachedStatus() {
  try {
    return JSON.parse(localStorage.getItem("afr_push_status") || "{}");
  } catch { return {}; }
}

/**
 * Live notifications status panel + "Send Test Notification" button.
 * - Reads frontend pipeline state (permission / token / SW) from PushNotifications
 *   via the `afr-push-status` window event + localStorage cache.
 * - Reads backend state (tokens registered, FCM enabled) via /notifications/status.
 * - Sends a real test push via /notifications/test.
 */
export default function NotificationStatusCard() {
  const [front, setFront] = useState(readCachedStatus());
  const [back, setBack] = useState(null);
  const [sending, setSending] = useState(false);
  const [loadingBack, setLoadingBack] = useState(true);

  useEffect(() => {
    const onStatus = (e) => setFront(e.detail || readCachedStatus());
    window.addEventListener("afr-push-status", onStatus);
    return () => window.removeEventListener("afr-push-status", onStatus);
  }, []);

  const loadBackend = async () => {
    setLoadingBack(true);
    try {
      const { data } = await api.get("/notifications/status");
      setBack(data);
    } catch {
      setBack({ ok: false });
    } finally {
      setLoadingBack(false);
    }
  };
  useEffect(() => { loadBackend(); }, []);

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/notifications/test");
      toast.success(`Test push sent (${data.sent})`, {
        description: "Check your device — it should arrive in seconds.",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test push");
    } finally {
      setSending(false);
      loadBackend();
    }
  };

  const perm = front?.permission || (typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const tokenOK = !!front?.tokenRegistered && (back?.tokens || 0) > 0;
  const allGreen = perm === "granted" && tokenOK && back?.fcmEnabled;

  return (
    <div className="border border-white/10 bg-[#111827] rounded-md p-4" data-testid="notif-status-card">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center border ${allGreen ? "border-[#3CFF7E]/40 bg-[#3CFF7E]/10 text-[#3CFF7E]" : "border-[#E6FF3C]/40 bg-[#E6FF3C]/10 text-[#E6FF3C]"}`}>
          {allGreen ? <Bell size={18} /> : <BellOff size={18} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-sm text-white">Notifications</div>
          <div className="text-[11px] text-[#9CA3AF]">
            {allGreen ? "Connected" : "Setup incomplete"}
          </div>
        </div>
      </div>

      <ul className="space-y-1.5 text-[12px] mb-3">
        <Row ok={perm === "granted"} label="Browser permission" value={perm} />
        <Row ok={!!front?.fcmSupported} label="FCM supported" value={front?.fcmSupported ? "yes" : "no"} />
        <Row ok={!!front?.installed} label="Running as installed PWA" value={front?.displayMode || "browser"} />
        <Row ok={!!front?.tokenAcquired} label="Token acquired" value={front?.tokenAcquired ? "yes" : "no"} />
        <Row
          ok={tokenOK}
          label="Registered with server"
          value={loadingBack ? "…" : `${back?.tokens || 0} token${(back?.tokens || 0) === 1 ? "" : "s"}`}
        />
        <Row
          ok={!!back?.installed}
          label="Subscription owner"
          value={loadingBack ? "…" : (back?.displayMode || "browser") + (back?.installed ? " (PWA)" : "")}
        />
        <Row ok={!!back?.fcmEnabled} label="Backend FCM" value={back?.fcmEnabled ? "ready" : "off"} />
      </ul>

      <button
        type="button"
        onClick={sendTest}
        disabled={sending || !tokenOK || !back?.fcmEnabled}
        data-testid="send-test-notif-btn"
        className="w-full h-11 bg-[#3CFF7E] text-[#0A0A0A] rounded-md font-display font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50"
      >
        {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        {sending ? "Sending…" : "Send Test Notification"}
      </button>
      {!tokenOK && (
        <div className="text-[11px] text-[#9CA3AF] mt-2 leading-snug">
          Enable notifications in your browser and reload to register a token. On Android, use the installed AutoFix PWA for best delivery.
        </div>
      )}
    </div>
  );
}

function Row({ ok, label, value }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-2 text-[#9CA3AF]">
        {ok ? <CheckCircle2 size={12} className="text-[#3CFF7E]" /> : <AlertCircle size={12} className="text-[#E6FF3C]" />}
        {label}
      </span>
      <span className="text-white font-mono text-[11px]">{value}</span>
    </li>
  );
}
